import { useAuth0 } from "ct-auth"
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material"
import { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { entitlementTypes, kpiAnalyticsEnabled, userHasEntitlement, userOrg } from "../../../data-utils/authUtils"
import { useQueries } from "../../../data-utils/reactQueryHelpers"
import { useOrgBranding } from "../../../hooks/useOrgBranding"
import { UiActions } from "../../../store/ui-slice"
import { DEFAULT_LOGO, DEFAULT_LOGO_BLACK, ROUTE_PATHS } from "../../../utilities/constants"
import SidePanelConfig from "./SidePanelConfig"
import PartnerIdentityLabels from "./PartnerIdentityLabels"
import UserMenu from "../UserMenu/UserMenu"

const isLinkActive = (activeLinks, currentLocation, excludeLinks) => {
  const loc = currentLocation.toLowerCase()
  if (Array.isArray(excludeLinks) && excludeLinks.some((l) => loc.startsWith(l))) return false
  const mainRoute = loc.split("/")[1]
  return activeLinks.includes(mainRoute) || (Array.isArray(activeLinks) && activeLinks.some((l) => loc.includes(l)))
}

function SidePanel({ currentLocation, userIsManager, userIsSponsor, userIsTrainer, userCanManageChildOrgs, userCanManageOrgs }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useAuth0()

  const isLeftPanelExpanded = useSelector((state) => state.ui.isLeftPanelExpanded)
  const isDarkMode = useSelector((state) => state.ui.isDarkMode)

  // Brand block at the top of the rail (matches the company's other product): the
  // org-branded logo (white/default asset reads correctly on the navy rail) + the
  // persisted dark/light toggle.
  const { data: orgData } = useQueries.HOME_USER_ORG({ orgId: userOrg(user) })
  const { logoUrl, isCustomLogo } = useOrgBranding(orgData)
  // Integration Hub is entitlement-gated: managers always; customers only if their org is granted it.
  const hasIntegrations = userHasEntitlement({ user, org: orgData, entitlementType: entitlementTypes.INTEGRATIONS })
  const hasKpi = kpiAnalyticsEnabled(orgData)
  const toggleUserTheme = useCallback(() => {
    dispatch(UiActions.setUserTheme(isDarkMode ? "light" : "dark"))
  }, [dispatch, isDarkMode])

  const sidePanelListItems = useMemo(
    () =>
      SidePanelConfig({ userIsManager, userIsSponsor, userIsTrainer, userCanManageChildOrgs, userCanManageOrgs, hasIntegrations, hasKpi }).map(
        (item) => {
          if (item.type === "link") {
            return {
              ...item,
              active: isLinkActive(item.activeLinks, currentLocation, item.excludeLinks),
              clickHandler: () => item.link && navigate(item.link),
            }
          }
          return { ...item }
        }
      ),
    [currentLocation, navigate, userIsManager, userIsSponsor, userIsTrainer, userCanManageChildOrgs, userCanManageOrgs, hasIntegrations, hasKpi]
  )

  return (
    // Column layout so the nav list grows and the partner-identity labels sit at the
    // very bottom of the panel (CT-1163). Fills the sidepanel's fixed height.
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box className={`side-panel-brand ${isLeftPanelExpanded ? "" : "collapsed"}`}>
        {isLeftPanelExpanded && (
          <Link to={ROUTE_PATHS.GAME_ROOT} className="side-panel-logo-link" title="Home">
            <img
              className={isCustomLogo ? "side-panel-logo side-panel-logo--custom" : "side-panel-logo"}
              // Permanent navy rail: always use the light logo (readable on navy) in both themes
              src={isCustomLogo ? logoUrl : DEFAULT_LOGO}
              onError={(e) => {
                if (e.currentTarget.dataset.fellBack) return
                e.currentTarget.dataset.fellBack = "1"
                e.currentTarget.src = DEFAULT_LOGO
              }}
              alt="brand logo"
            />
          </Link>
        )}
        <button
          type="button"
          className="side-panel-theme-toggle no-print"
          onClick={toggleUserTheme}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          <i className={`fa-solid ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
        </button>
      </Box>
      <List
        className="side-panel-list"
        sx={{
          paddingTop: (theme) => theme.spacing(2),
          flex: 1,
          overflowY: "auto",
        }}
      >
        {sidePanelListItems.map(
          (item, index) =>
            item.visible &&
            (item.type === "link" ? (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={!isLeftPanelExpanded ? item.text : undefined} placement="right" arrow>
                  <ListItemButton
                    className={`list-button ${item.active ? "active" : ""}`}
                    onClick={() => item.clickHandler(item)}
                    disabled={item.disabled}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    {isLeftPanelExpanded && (
                      <ListItemText>
                        <span>{item.text}</span>
                        {item.active && <span className="active-indicator">&nbsp;</span>}
                      </ListItemText>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ) : item.type === "label" ? (
              // Section label (CT Modern rail). Collapses to a divider when the rail is
              // collapsed so grouping stays visible without text overflow.
              isLeftPanelExpanded ? (
                <li key={`label-${item.text}`} className="side-panel-section-label" aria-hidden="true">
                  {item.text}
                </li>
              ) : (
                <Divider key={`label-${item.text}`} />
              )
            ) : item.type === "divider" ? (
              <Divider key={`divider-${index}`} />
            ) : null)
        )}
      </List>
      {/* Account menu in the rail footer (CT Modern shell — was the top-header dropdown) */}
      <UserMenu expanded={isLeftPanelExpanded} />
      <PartnerIdentityLabels expanded={isLeftPanelExpanded} />
    </Box>
  )
}

export default SidePanel
